import * as THREE from 'three/webgpu';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import type { PointSnapResult } from './pointSnap/PointSnapTypes';
import { PointSnapStrategyType } from './pointSnap/PointSnapStrategyType';
import type { Viewer } from '../viewer';
import { isSketchPointObject, SketchPointKind } from '../mesh/sketch';

/**
 * 鼠标周围标签方位枚举。
 */
export enum PointerNearbyLabelPosition {
    /** 左上。 */
    LeftTop = 'left-top',
    /** 右上。 */
    RightTop = 'right-top',
    /** 左下。 */
    LeftBottom = 'left-bottom',
    /** 右下。 */
    RightBottom = 'right-bottom',
}

/**
 * 文本标签显示选项。
 */
export interface PointerNearbyTextLabelOptions {
    /** 容器类名。 */
    className?: string;
    /** 内联样式。 */
    styles?: Record<string, string>;
}

/**
 * 内部槽位状态。
 */
interface PointerNearbyLabelSlotState {
    /** CSS2D 标签对象。 */
    object: CSS2DObject;
    /** 容器元素。 */
    container: HTMLDivElement;
    /** 文本元素。 */
    textElement: HTMLDivElement | null;
    /** 主图标元素。 */
    primaryIconElement: HTMLImageElement | null;
    /** 辅助图标元素。 */
    secondaryIconElement: HTMLImageElement | null;
    /** 当前主图标地址。 */
    currentPrimaryIconSrc: string;
    /** 当前辅助图标地址。 */
    currentSecondaryIconSrc: string;
}

/**
 * 捕捉策略类型到图标 SVG 路径的映射表。
 */
const POINT_SNAP_ICON_MAP: Readonly<Partial<Record<PointSnapStrategyType, string>>> = {
    /** 端点/草图原点捕捉图标：双同心圆；独立草图点在显示入口按目标类型区分。 */
    [PointSnapStrategyType.EndpointOrigin]: '/textures/sketch/snap-endpoint.svg',
    /** 中点捕捉图标：三角形。 */
    [PointSnapStrategyType.Midpoint]: '/textures/sketch/snap-midpoint.svg',
    /** 圆心捕捉图标：同心圆。 */
    [PointSnapStrategyType.Center]: '/textures/sketch/snap-center.svg',
    /** 象限点捕捉图标：菱形。 */
    [PointSnapStrategyType.Quadrant]: '/textures/sketch/snap-quadrant.svg',
    /** 交叉点捕捉图标：X 叉号。 */
    [PointSnapStrategyType.Intersection]: '/textures/sketch/snap-intersection.svg',
    /** 最近点捕捉图标：圆圈加三个分叉，同时用于独立草图点提示。 */
    [PointSnapStrategyType.Nearest]: '/textures/sketch/snap-nearest.svg',
    /** 悬停在圆弧边图标：圆弧形。 */
    [PointSnapStrategyType.OnArc]: '/textures/sketch/snap-on-arc.svg',
    /** 角度捕捉图标：角度符号。 */
    [PointSnapStrategyType.Angle]: '/textures/sketch/snap-angle.svg',
};

/** 默认捕捉图标路径。 */
const POINT_SNAP_DEFAULT_ICON = '/textures/sketch/snap-default.svg';

/**
 * 鼠标周围标签管理器。
 * 说明：
 * 1. 统一管理鼠标附近四个方位的 CSS2D 标签对象；
 * 2. 支持文本标签与图标标签两类表现；
 * 3. 当前用于承接多边形角度标签（右上）与点捕捉指示器（右下）；
 * 4. 资源生命周期统一挂在 Viewer 上，避免各模块重复持有独立标签对象。
 */
export class PointerNearbyLabelManager {
    /** Viewer 实例。 */
    private readonly viewer: Viewer;

    /** 四象限槽位状态映射。 */
    private readonly slotMap: Map<PointerNearbyLabelPosition, PointerNearbyLabelSlotState> = new Map();

    /**
     * 构造函数。
     * @param viewer Viewer 实例。
     */
    public constructor(viewer: Viewer) {
        this.viewer = viewer;
    }

    /**
     * 显示指定方位的文本标签。
     * @param position 标签锚点世界坐标。
     * @param labelPosition 标签方位。
     * @param text 文本内容。
     * @param options 文本显示选项。
     */
    public showTextLabel(
        position: THREE.Vector3,
        labelPosition: PointerNearbyLabelPosition,
        text: string,
        options: PointerNearbyTextLabelOptions = {},
    ): void {
        const slotState = this.ensureSlot(labelPosition);
        const textElement = this.ensureTextElement(slotState);
        slotState.container.className = options.className ?? 'pointer-nearby-text-label';
        textElement.textContent = text;
        this.applyTextStyles(textElement, options.styles ?? {});
        this.hidePrimaryIconElement(slotState);
        this.hideSecondaryIconElement(slotState);
        slotState.object.position.copy(position);
        slotState.object.visible = true;
    }

    /**
     * 显示点捕捉主图标。
     * 根据最终捕捉目标区分独立草图点与端点，统一绘制和非绘制命令的提示，保持捕捉语义不变。
     * @param result 捕捉结果。
     * @param iconOverride 可选图标覆盖路径。
     */
    public showPointSnapIndicator(result: PointSnapResult, iconOverride?: string): void {
        const slotState = this.ensureSlot(PointerNearbyLabelPosition.RightBottom);
        const primaryIconElement = this.ensurePrimaryIconElement(slotState);
        slotState.container.className = 'snap-indicator';
        // 会话原点和模型投影点可能使用草图点类型，必须排除，避免参考点误用独立草图点图标。
        const target = result.targetObject;
        const isStandaloneSketchPoint = result.strategyType === PointSnapStrategyType.EndpointOrigin
            && isSketchPointObject(target)
            && target.pointKind === SketchPointKind.SketchPoint
            && !target.isDrawingSketchOrigin
            && !target.isSketchSessionReferencePoint
            && !target.isDrawingPreviewPoint;
        // 仅复用三分叉图标资源，不把端点策略改为最近点策略；显式图标覆盖仍优先。
        const defaultIcon = POINT_SNAP_ICON_MAP[
            isStandaloneSketchPoint ? PointSnapStrategyType.Nearest : result.strategyType
        ];
        const iconSrc = iconOverride
            ?? result.indicatorIconOverride
            ?? defaultIcon
            ?? POINT_SNAP_DEFAULT_ICON;
        if (slotState.currentPrimaryIconSrc !== iconSrc) {
            primaryIconElement.src = iconSrc;
            slotState.currentPrimaryIconSrc = iconSrc;
        }
        primaryIconElement.style.display = '';
        this.hideTextElement(slotState);
        slotState.object.position.copy(result.indicatorPosition ?? result.position);
        slotState.object.visible = true;
    }

    /**
     * 显示点捕捉辅助图标。
     * @param iconSrc 辅助图标路径。
     */
    public showPointSnapSecondaryIndicator(iconSrc: string): void {
        const slotState = this.ensureSlot(PointerNearbyLabelPosition.RightBottom);
        const secondaryIconElement = this.ensureSecondaryIconElement(slotState);
        slotState.container.className = 'snap-indicator';
        if (slotState.currentSecondaryIconSrc !== iconSrc) {
            secondaryIconElement.src = iconSrc;
            slotState.currentSecondaryIconSrc = iconSrc;
        }
        secondaryIconElement.style.display = '';
        slotState.object.visible = true;
    }

    /**
     * 隐藏点捕捉辅助图标。
     */
    public hidePointSnapSecondaryIndicator(): void {
        const slotState = this.slotMap.get(PointerNearbyLabelPosition.RightBottom);
        if (!slotState) {
            return;
        }
        this.hideSecondaryIconElement(slotState);
    }

    /**
     * 隐藏指定方位的标签。
     * @param labelPosition 标签方位。
     */
    public hideLabel(labelPosition: PointerNearbyLabelPosition): void {
        const slotState = this.slotMap.get(labelPosition);
        if (!slotState) {
            return;
        }
        slotState.object.visible = false;
        if (labelPosition === PointerNearbyLabelPosition.RightBottom) {
            this.hideSecondaryIconElement(slotState);
        }
    }

    /**
     * 隐藏全部标签。
     */
    public hideAllLabels(): void {
        for (const labelPosition of this.slotMap.keys()) {
            this.hideLabel(labelPosition);
        }
    }

    /**
     * 销毁全部标签资源。
     */
    public dispose(): void {
        for (const slotState of this.slotMap.values()) {
            this.viewer.scene.remove(slotState.object);
            if (slotState.container.parentNode) {
                slotState.container.parentNode.removeChild(slotState.container);
            }
        }
        this.slotMap.clear();
    }

    /**
     * 应用文本标签默认样式与扩展样式。
     * @param textElement 文本元素。
     * @param styles 扩展样式。
     */
    private applyTextStyles(textElement: HTMLDivElement, styles: Record<string, string>): void {
        textElement.style.color = '#000000';
        textElement.style.fontSize = '12px';
        textElement.style.fontWeight = 'bold';
        textElement.style.backgroundColor = 'rgba(255,255,255,0.7)';
        textElement.style.padding = '1px 3px';
        textElement.style.borderRadius = '2px';
        textElement.style.pointerEvents = 'none';
        textElement.style.marginLeft = '12px';
        Object.entries(styles).forEach(([key, value]) => {
            textElement.style.setProperty(key, value);
        });
        textElement.style.display = '';
    }

    /**
     * 获取或创建指定方位的槽位。
     * @param labelPosition 标签方位。
     * @returns 槽位状态。
     */
    private ensureSlot(labelPosition: PointerNearbyLabelPosition): PointerNearbyLabelSlotState {
        const existingSlot = this.slotMap.get(labelPosition);
        if (existingSlot) {
            return existingSlot;
        }

        const container = document.createElement('div');
        container.style.pointerEvents = 'none';
        const object = new CSS2DObject(container);
        const anchor = this.resolveAnchor(labelPosition);
        object.center.set(anchor.x, anchor.y);
        object.visible = false;
        this.viewer.addObject(object);

        const slotState: PointerNearbyLabelSlotState = {
            object,
            container,
            textElement: null,
            primaryIconElement: null,
            secondaryIconElement: null,
            currentPrimaryIconSrc: '',
            currentSecondaryIconSrc: '',
        };
        this.slotMap.set(labelPosition, slotState);
        return slotState;
    }

    /**
     * 根据方位解析 CSS2D 锚点。
     * @param labelPosition 标签方位。
     * @returns 锚点坐标。
     */
    private resolveAnchor(labelPosition: PointerNearbyLabelPosition): THREE.Vector2 {
        switch (labelPosition) {
            case PointerNearbyLabelPosition.LeftTop:
                return new THREE.Vector2(1, 1);
            case PointerNearbyLabelPosition.RightTop:
                return new THREE.Vector2(0, 1);
            case PointerNearbyLabelPosition.LeftBottom:
                return new THREE.Vector2(1, 0);
            case PointerNearbyLabelPosition.RightBottom:
                return new THREE.Vector2(0, 0);
            default:
                throw new Error(`未知鼠标周围标签方位：${String(labelPosition)}`);
        }
    }

    /**
     * 获取或创建文本元素。
     * @param slotState 槽位状态。
     * @returns 文本元素。
     */
    private ensureTextElement(slotState: PointerNearbyLabelSlotState): HTMLDivElement {
        if (!slotState.textElement) {
            const textElement = document.createElement('div');
            slotState.container.appendChild(textElement);
            slotState.textElement = textElement;
        }
        return slotState.textElement;
    }

    /**
     * 获取或创建主图标元素。
     * @param slotState 槽位状态。
     * @returns 主图标元素。
     */
    private ensurePrimaryIconElement(slotState: PointerNearbyLabelSlotState): HTMLImageElement {
        if (!slotState.primaryIconElement) {
            const primaryIconElement = document.createElement('img');
            primaryIconElement.classList.add('snap-indicator-icon');
            primaryIconElement.draggable = false;
            slotState.container.appendChild(primaryIconElement);
            slotState.primaryIconElement = primaryIconElement;
        }
        return slotState.primaryIconElement;
    }

    /**
     * 获取或创建辅助图标元素。
     * @param slotState 槽位状态。
     * @returns 辅助图标元素。
     */
    private ensureSecondaryIconElement(slotState: PointerNearbyLabelSlotState): HTMLImageElement {
        if (!slotState.secondaryIconElement) {
            const secondaryIconElement = document.createElement('img');
            secondaryIconElement.classList.add('snap-indicator-icon', 'snap-indicator-icon-secondary');
            secondaryIconElement.draggable = false;
            secondaryIconElement.style.display = 'none';
            slotState.container.appendChild(secondaryIconElement);
            slotState.secondaryIconElement = secondaryIconElement;
        }
        return slotState.secondaryIconElement;
    }

    /**
     * 隐藏文本元素。
     * @param slotState 槽位状态。
     */
    private hideTextElement(slotState: PointerNearbyLabelSlotState): void {
        if (slotState.textElement) {
            slotState.textElement.style.display = 'none';
        }
    }

    /**
     * 隐藏主图标元素。
     * @param slotState 槽位状态。
     */
    private hidePrimaryIconElement(slotState: PointerNearbyLabelSlotState): void {
        if (slotState.primaryIconElement) {
            slotState.primaryIconElement.style.display = 'none';
        }
        slotState.currentPrimaryIconSrc = '';
    }

    /**
     * 隐藏辅助图标元素。
     * @param slotState 槽位状态。
     */
    private hideSecondaryIconElement(slotState: PointerNearbyLabelSlotState): void {
        if (slotState.secondaryIconElement) {
            slotState.secondaryIconElement.style.display = 'none';
        }
        slotState.currentSecondaryIconSrc = '';
    }
}
